import type { RoleRecord } from '@/types/portal';
import FilterSelect from '@/components/base/FilterSelect';
import MarketSelect from '@/components/base/MarketSelect';

const statusIcons: Record<string, string> = {
  All: 'ri-stack-line',
  Active: 'ri-user-follow-line',
  Invited: 'ri-mail-send-line',
  Deactivated: 'ri-user-unfollow-line',
};

interface UserToolbarProps {
  tabs: { id: string; label: string; count: number }[];
  status: string;
  onStatus: (id: string) => void;
  roleId: string;
  onRole: (value: string) => void;
  /** The agency's roles, for the role filter. */
  roleRecords: RoleRecord[];
  markets: string[];
  onSetMarkets: (value: string[]) => void;
  query: string;
  onQuery: (value: string) => void;
  twoFactorOnly: boolean;
  twoFactorCount: number;
  onToggleTwoFactor: () => void;
}

export default function UserToolbar({
  tabs,
  status,
  onStatus,
  roleId,
  onRole,
  roleRecords,
  markets,
  onSetMarkets,
  query,
  onQuery,
  twoFactorOnly,
  twoFactorCount,
  onToggleTwoFactor,
}: UserToolbarProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <FilterSelect
          value={status}
          onChange={onStatus}
          label="Status"
          className="w-full sm:w-56"
          options={tabs.map((tab) => ({ ...tab, icon: statusIcons[tab.id] }))}
        />

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <button
            type="button"
            aria-pressed={twoFactorOnly}
            onClick={onToggleTwoFactor}
            className={`flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-sm font-label font-medium transition-colors ${
              twoFactorOnly
                ? 'border-accent-500 bg-accent-500 text-background-50'
                : 'border-accent-200 bg-accent-100 text-accent-900 hover:border-accent-400'
            }`}
          >
            <i className="ri-shield-check-line" />
            With 2FA
            <span
              className={`rounded-full px-1.5 text-[11px] font-semibold ${
                twoFactorOnly
                  ? 'bg-background-50/25 text-background-50'
                  : 'bg-background-50 text-accent-900'
              }`}
            >
              {twoFactorCount}
            </span>
          </button>

          <div className="relative">
            <span className="sr-only">Filter by role</span>
            <select
              value={roleId}
              onChange={(event) => onRole(event.target.value)}
              className="h-9 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-8 text-sm font-label text-foreground-700 focus:border-primary-400 focus:outline-none sm:w-48"
            >
              <option value="All roles">All roles</option>
              {roleRecords.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>

          <MarketSelect
            value={markets}
            onChange={onSetMarkets}
            placeholder="All markets"
            compact
            className="sm:w-52"
          />

          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
            <i className="ri-search-line text-foreground-400" />
            <input
              type="text"
              value={query}
              onChange={(event) => onQuery(event.target.value)}
              placeholder="Search name, email or role..."
              className="h-9 w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none sm:w-60"
            />
          </div>
        </div>
      </div>
    </div>
  );
}